import { ChatPane, QuickReply, UiStrings } from "./pane";
import { WidgetConfig } from "./remote_config";
import { ChatMessage, ChatSession } from "./session";

/** Told when it is known whether the visitor's chat has begun, and when
 *  that changes. `loaded`: it is what the page found on load (their
 *  history), shown as it is; otherwise the visitor or a reply began it
 *  just now. */
export type BegunListener = (begun: boolean, loaded: boolean) => void;

/** How long the first message waits for a stream that is still opening:
 *  the reply must find it subscribed. A stream that doesn't open in time
 *  catches up on what it missed once it does (ChatSession.opened). */
const STREAM_OPEN_WAIT_MS = 5000;

/**
 * The page's one chat with the backend, shown in every chat window the
 * page has: the floating bubble's popover and any window placed in the
 * page. One ChatSession, one message list; a window that attaches later
 * gets everything shown so far. The visitor's `source_id` is the same on
 * every page, so moving between pages, or between the bubble and a page
 * window, carries on the same conversation.
 *
 * Two ways in:
 *   - open()        the bubble opened: POST /session, GET /history, stream.
 *                   As it always was — opening the bubble starts the chat.
 *   - showHistory() a page window appeared: GET /history only. Nothing is
 *                   created for a visitor who hasn't chatted yet (404 → the
 *                   greeting); /session waits for their first message.
 */
export class Conversation {
    readonly #session: ChatSession;
    readonly #config: WidgetConfig;
    readonly #ui: UiStrings;
    readonly #panes = new Set<ChatPane>();
    /** Everything shown so far, in order, for a window that attaches
     *  later. */
    readonly #shown: ChatMessage[] = [];
    /** Bot messages the visitor answered with their chips or input. */
    readonly #answered = new Set<string>();
    /** The welcome quick replies, until the visitor sends anything. */
    #options: QuickReply[] = [];
    #typing = false;
    #greetingShown = false;
    /** Fetching or showing the history (or the greeting), once. */
    #history?: Promise<void>;
    /** The stream is connected: replies arrive. */
    #hydrated = false;
    #starting?: Promise<boolean>;
    /** The window the visitor last used: an input a reply brings may
     *  take the focus there, nowhere else. */
    #active?: ChatPane;
    /** The widget was torn down (destroy, reset): a history or session
     *  request still out must not open a stream when it comes back. */
    #closed = false;
    /** The visitor's chat has begun: they sent something, or there is a
     *  conversation to show. Until then a page window is compact.
     *  Undefined while that isn't known yet: the history is loading. */
    #begun: boolean | undefined;
    /** Something on this page began it: the visitor sent a message, or
     *  a reply came in. A history that comes back empty after that
     *  doesn't undo it. */
    #begunHere = false;
    readonly #begunListeners = new Set<BegunListener>();

    constructor(params: {
        session: ChatSession;
        config: WidgetConfig;
        ui: UiStrings;
        /** This browser saw the visitor's chat begin (storage.ts): taken
         *  as begun until the history says otherwise, so a page window
         *  opens at full height at once instead of a moment later. */
        begunBefore?: boolean;
    }) {
        this.#session = params.session;
        this.#config = params.config;
        this.#ui = params.ui;
        this.#begun = params.begunBefore ? true : undefined;
    }

    /** Whether the visitor's chat has begun; undefined until that is
     *  known (see #begun). */
    get begun(): boolean | undefined {
        return this.#begun;
    }

    /** Calls `fn` whenever `begun` changes, also when it first becomes
     *  known. Returns the function that stops it. */
    onBegunChange(fn: BegunListener): () => void {
        this.#begunListeners.add(fn);
        return () => {
            this.#begunListeners.delete(fn);
        };
    }

    /** The visitor last used `pane`: what they sent went from there. */
    isActive(pane: ChatPane): boolean {
        return this.#active === pane;
    }

    /** Shows the chat so far in `pane`, and from now on every change. */
    attach(pane: ChatPane): void {
        this.#panes.add(pane);
        for (const msg of this.#shown) pane.show(msg, false);
        for (const id of this.#answered) pane.answered(id);
        if (this.#options.length > 0) pane.renderOptions(this.#options);
        if (this.#typing) pane.setTyping(true);
        this.#announce();
    }

    detach(pane: ChatPane): void {
        this.#panes.delete(pane);
        this.leave(pane);
    }

    /** Another window than `pane` shows the chat: one in the page. */
    hasOtherWindow(pane: ChatPane): boolean {
        for (const other of this.#panes) if (other !== pane) return true;
        return false;
    }

    /** The visitor is in `pane` now (opened it, or moved into it). */
    activate(pane: ChatPane): void {
        this.#active = pane;
        this.#announce();
    }

    /** The visitor left `pane` (closed the bubble): no window is theirs
     *  until they use one again. */
    leave(pane: ChatPane): void {
        if (this.#active !== pane) return;
        this.#active = undefined;
        this.#announce();
    }

    /** The widget is torn down: no stream opens anymore, also not for a
     *  request that is still out. */
    close(): void {
        this.#closed = true;
        this.#session.disconnect();
    }

    /**
     * Starts the chat: session, then the history (or the greeting), then
     * the stream. Resolves false when the session could not start; the
     * next call tries again. `failureText` is what the visitor reads then.
     */
    start(failureText = this.#ui.startFailed): Promise<boolean> {
        if (this.#hydrated) return Promise.resolve(true);
        this.#starting ??= this.#start(failureText).finally(() => {
            this.#starting = undefined;
        });
        return this.#starting;
    }

    /**
     * What a page window shows before the visitor writes: their open
     * conversation, or the greeting and quick replies. Creates nothing.
     * A visitor with history gets the stream right away, so a reply
     * (the bot's, or a colleague taking over) shows up as it comes.
     */
    showHistory(): Promise<void> {
        this.#history ??= (async () => {
            let messages: ChatMessage[] | undefined;
            try {
                messages = await this.#session.existingHistory();
            } catch (err) {
                // Nothing to show then; the first message starts a
                // session, which picks the conversation up again.
                console.warn("AmaiChat: loading the chat history failed", err);
            }
            if (messages && messages.length > 0) {
                this.#setBegun(true, true);
                for (const m of messages) this.#show(m, false);
                this.#connect();
            } else {
                this.#setBegun(this.#begunHere, true);
                this.#showGreeting();
            }
        })();
        return this.#history;
    }

    /**
     * Sends what the visitor wrote (or picked) in `from`. Shows it at
     * once in every window; a chat that hasn't started yet starts first.
     */
    async send(
        text: string,
        from?: ChatPane,
        answering?: string,
    ): Promise<void> {
        const trimmed = text.trim();
        if (!trimmed) return;
        if (from) this.#active = from;
        // At once, before anything changes in the windows: a page window
        // grows from the size it has on screen now.
        this.#beganHere();
        // A page window may still be loading the history: the message
        // goes below it, not above. (A failed load is the start's to
        // report.)
        await this.#history?.catch(() => undefined);
        if (answering && !this.#answered.has(answering)) {
            this.#answered.add(answering);
            for (const pane of this.#panes) pane.answered(answering);
        }
        // The welcome quick replies are a one-shot entry point, not a
        // persistent menu: gone as soon as the visitor sends anything
        // (a quick reply, a typed message, an inline answer).
        this.#setOptions([]);
        this.#show(
            {
                id: "_local-" + Date.now(),
                role: "visitor",
                content: trimmed,
                content_type: "text",
                content_attributes: {},
            },
            false,
        );
        this.#setTyping(true);
        if (!this.#hydrated) {
            // The first message of a page window's chat: only now does
            // the visitor get a session (and a conversation).
            if (!(await this.start(this.#ui.sendFailed))) {
                this.#setTyping(false);
                return;
            }
            await this.#session.opened(STREAM_OPEN_WAIT_MS);
        }
        try {
            await this.#session.send(trimmed);
        } catch (err) {
            this.#setTyping(false);
            console.warn("AmaiChat: send failed", err);
            const serverMessage = (
                err as { serverMessage?: string } | undefined
            )?.serverMessage;
            this.#show(
                {
                    id: "_send-error-" + Date.now(),
                    role: "system",
                    content: serverMessage || this.#ui.sendFailed,
                    content_type: "text",
                    content_attributes: {},
                },
                false,
            );
        }
    }

    async #start(failureText: string): Promise<boolean> {
        try {
            await this.#session.start();
            // A page window may have fetched the history already (or be
            // fetching it): shown once, whoever got there first.
            if (this.#history) {
                await this.#history;
            } else {
                this.#history = this.#showStartHistory();
                await this.#history;
            }
        } catch (err) {
            // Without a session there is nothing to stream: the SSE
            // endpoint 404s (and EventSource would retry it forever),
            // and replies to any typed message could never arrive —
            // the visitor would stare at typing dots. Tell them, and
            // stay unhydrated so the next open or message retries the
            // whole start sequence.
            console.warn("AmaiChat: session start failed", err);
            this.#show(
                {
                    id: "_start-error-" + Date.now(),
                    role: "system",
                    content: failureText,
                    content_type: "text",
                    content_attributes: {},
                },
                false,
            );
            return false;
        }
        this.#connect();
        return true;
    }

    async #showStartHistory(): Promise<void> {
        try {
            const history = await this.#session.history();
            this.#setBegun(history.length > 0 || this.#begunHere, true);
            for (const m of history) this.#show(m, true);
            if (history.length === 0) this.#showGreeting();
        } catch (err) {
            // Not fetched: the next start asks again.
            this.#history = undefined;
            throw err;
        }
    }

    #connect(): void {
        if (this.#hydrated || this.#closed) return;
        this.#session.connect((msg) => {
            // Drop messages we already showed via local echo (visitor role
            // from this widget instance). Bot/agent replies always render.
            if (msg.role === "visitor") return;
            this.#beganHere();
            this.#setTyping(false);
            this.#show(msg, true);
        });
        this.#hydrated = true;
    }

    #showGreeting(): void {
        if (this.#greetingShown) return;
        this.#greetingShown = true;
        this.#show(
            {
                id: "_greeting",
                role: "bot",
                content: this.#config.greeting,
                content_type: "text",
                content_attributes: {},
            },
            false,
        );
        this.#setOptions(this.#config.options);
    }

    /** `focus`: an input the message carries may take the focus, in the
     *  window the visitor is using. Never for what a window shows on
     *  load. */
    #show(msg: ChatMessage, focus: boolean): void {
        this.#shown.push(msg);
        for (const pane of this.#panes) {
            pane.show(msg, focus && pane === this.#active);
        }
    }

    /** A screen reader reads each message once: in the window the
     *  visitor is in, or in every window while they are in none (the
     *  bubble's closed popover is hidden from it anyway). */
    #announce(): void {
        for (const pane of this.#panes) {
            pane.setLive(!this.#active || pane === this.#active);
        }
    }

    #beganHere(): void {
        this.#begunHere = true;
        this.#setBegun(true, false);
    }

    #setBegun(begun: boolean, loaded: boolean): void {
        if (this.#begun === begun) return;
        this.#begun = begun;
        for (const fn of this.#begunListeners) fn(begun, loaded);
    }

    #setTyping(on: boolean): void {
        this.#typing = on;
        for (const pane of this.#panes) pane.setTyping(on);
    }

    #setOptions(options: QuickReply[]): void {
        this.#options = options;
        for (const pane of this.#panes) pane.renderOptions(options);
    }
}
